import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { useApolloClient, useMutation, useLazyQuery } from "@apollo/client";
import { useEventListener } from "@magento/peregrine";
import { clearCartDataFromCache } from "@magento/peregrine/lib/Apollo/clearCartDataFromCache";
import { clearCustomerDataFromCache } from "@magento/peregrine/lib/Apollo/clearCustomerDataFromCache";
import { useCartContext } from "@magento/peregrine/lib/context/cart";
import { useUserContext } from "@magento/peregrine/lib/context/user";
import { useAwaitQuery } from "@magento/peregrine/lib/hooks/useAwaitQuery";
import { retrieveCartId } from "@magento/peregrine/lib/store/actions/cart";
import SIGNIN_OPERATIONS from "@overrides_venia/peregrine/talons/SignIn/signIn.gql.js";
import mergeOperations from "@magento/peregrine/lib/util/shallowMerge";
import { useCallback } from "react";
import { useCheckoutContext } from "@magento/peregrine/lib/context/checkout";
import CheckoutPageOperations from "@overrides_venia/venia-ui/components/CheckoutPage/checkoutPage.gql";
import { format } from "date-fns";
import { env } from "build/env";
export var useSignInEvent = function () {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var {
    handleErrors
  } = props;
  var operations = mergeOperations(SIGNIN_OPERATIONS, props.operations);
  var {
    createCartMutation,
    getCustomerQuery,
    mergeCartsMutation
  } = operations;
  var {
    mutations: {
      calculateLSCartMutation,
      setTakeawayDataOnCart
    },
    queries: {
      getCheckoutDetailsQuery
    }
  } = CheckoutPageOperations;
  var apolloClient = useApolloClient();
  var [{
    cartId
  }, {
    createCart,
    removeCart,
    updateCartData
  }] = useCartContext();
  var [{
    isGettingDetails,
    isSignedIn,
    isSigningIn
  }, {
    getUserDetails,
    setToken,
    submitIsSigningIn
  }] = useUserContext();
  var [{
    pickupTime
  }, {
    submitCheckoutStepCompleted
  }] = useCheckoutContext();
  var [setDataOnCart] = useMutation(setTakeawayDataOnCart);
  var [fetchCartId] = useMutation(createCartMutation);
  var [mergeCarts] = useMutation(mergeCartsMutation);
  var [calculateLSCart] = useMutation(calculateLSCartMutation);
  var [getCheckoutDetails] = useLazyQuery(getCheckoutDetailsQuery, {
    fetchPolicy: "no-cache",
    skip: !cartId,
    onError: error => {
      console.log(error);
    }
  });
  var fetchUserDetails = useAwaitQuery(getCustomerQuery);
  var socialLoginEventHandler = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(function* (event) {
      var backendUrl = new URL(env.MAGENTO_BACKEND_URL).host;
      var originUrl = event.origin && event.origin !== "null" ? new URL(event.origin).host : null;
      if (originUrl !== backendUrl && event.type !== "loginsocial") {
        return null;
      }
      try {
        if (isSignedIn) {
          return null;
        }
        var {
          token,
          result,
          messages,
          redirect_data
        } = (event.type === "loginsocial" ? event.detail : event.data) || {};
        if (result === 1 && token) {
          submitIsSigningIn(true);
          var sourceCartId = cartId;
          yield setToken(token);

          // Clear all cart/customer data from cache and redux.
          yield clearCartDataFromCache(apolloClient);
          yield clearCustomerDataFromCache(apolloClient);
          yield removeCart();
          getUserDetails({
            fetchUserDetails
          });
          // Create and get the customer's cart id.
          submitCheckoutStepCompleted({
            pickupTime: false,
            shippingAddress: false,
            payment: false,
            review: false,
            tableData: false,
            shippingMethod: false,
            shippingDataSet: false
          });
          yield createCart({
            fetchCartId
          });
          var destinationCartId = yield retrieveCartId();

          // Merge the guest cart into the customer cart.
          yield mergeCarts({
            variables: {
              destinationCartId,
              sourceCartId
            }
          });
          if (tableData.store) {
            var _tableData$store, _tableData;
            var selectedPickupDateTime = pickupTime !== null && pickupTime !== void 0 && pickupTime.date && pickupTime !== null && pickupTime !== void 0 && pickupTime.hour && typeof (pickupTime === null || pickupTime === void 0 ? void 0 : pickupTime.hour) === "string" ? pickupTime.date + "T" + pickupTime.hour + ":00" : null;
            var currentISOTimestampAdjString = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
            yield setDataOnCart({
              variables: {
                cart_id: destinationCartId,
                pickup_date: selectedPickupDateTime ? selectedPickupDateTime : currentISOTimestampAdjString,
                pickup_time: selectedPickupDateTime ? selectedPickupDateTime : currentISOTimestampAdjString,
                ls_store_id: (_tableData$store = tableData.store) === null || _tableData$store === void 0 ? void 0 : _tableData$store.id,
                ls_covers_quantity: 0,
                ls_sales_type: tableData.sales_type,
                ls_table_number: (_tableData = tableData) === null || _tableData === void 0 ? void 0 : _tableData.table
              }
            });
            yield calculateLSCart({
              variables: {
                cartId: destinationCartId
              }
            });
          }
          yield getCheckoutDetails({
            variables: {
              cartId: destinationCartId
            },
            onCompleted: data => {
              updateCartData(data.cart);
              submitIsSigningIn(false);
            }
          });
          try {
            UIkit.offcanvas("#offcanvas-login-dx").hide();
            UIkit.offcanvas("#offcanvas-registration-dx").hide();
          } catch (error) {
            console.error(error);
          }
        } else if (result === 0) {
          console.error("social-login-error", messages, redirect_data);
        }
      } catch (error) {
        console.error(error);
        submitIsSigningIn(false);
      }
    });
    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [cartId, apolloClient, removeCart, setToken, createCart, fetchCartId, mergeCarts, getUserDetails, fetchUserDetails, isSignedIn, handleErrors]);
  useEventListener(window, "message", socialLoginEventHandler);
  useEventListener(window, "loginsocial", socialLoginEventHandler);
  return {
    isBusy: isGettingDetails || isSigningIn,
    isGettingDetails: isGettingDetails
  };
};